import React, { useEffect, useState } from 'react';
import api from '../../../api'; // Adjust path if necessary, or pass api instance as prop
import { formatDateTime } from '../../../utils/formatDate';
// Depending on where this component is, the import path to api might need adjustment. 
// Standardizing to use the one from src/api.js

const UserDomainsModal = ({ show, onClose, userId, email }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDomainDetails, setSelectedDomainDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);

  useEffect(() => {
    if (show && userId) {
      fetchDomains();
    } else {
      setDomains([]);
      setSelectedDomainDetails(null);
    }
    // eslint-disable-next-line
  }, [show, userId]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/domains', { params: { userId } });

      if (response.data.success) {
        setDomains(response.data.data.domains || []);
      }
    } catch (error) {
      console.error('Failed to fetch domains', error);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainDetails = async (domainName) => {
    try {
      setLoadingDetails(true);
      setShowRawJson(false);
      const response = await api.get(`/api/admin/domains/${domainName}/details`);
      if (response.data.success) {
        setSelectedDomainDetails(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch domain details', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show block bg-[rgba(0,0,0,0.5)]">
      <div className="modal-dialog modal-lg">
        <div className="modal-content border-0 shadow">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold">
              Domains for User: <span className="text-primary">{email || userId}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : domains.length > 0 ? (
              <div className="row">
                <div className="col-md-5 border-end max-h-[60vh] overflow-y-auto">
                  <div className="list-group list-group-flush">
                    {domains.map((domain) => (
                      <button
                        key={domain.id}
                        className={`list-group-item list-group-item-action ${selectedDomainDetails?.id === domain.id ? 'active' : ''}`}
                        onClick={() => fetchDomainDetails(domain.name)}
                      >
                        <i className={`las la-globe me-2 ${domain.dns_status === 'connected' ? 'text-success' : 'text-warning'}`}></i>
                        <span className="d-flex flex-column align-items-start">
                          <span>{domain.name}</span>
                          <small className={domain.dns_status === 'connected' ? 'text-success' : 'text-warning'}>
                            {domain.dns_status === 'connected' ? 'Verified' : (domain.dns_status || 'Unverified')}
                          </small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-md-7">
                  {loadingDetails ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-muted" role="status"></div>
                    </div>
                  ) : selectedDomainDetails ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Details for {selectedDomainDetails.name}</h6>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={showRawJson}
                            onChange={(e) => setShowRawJson(e.target.checked)}
                          />
                          <label className="form-check-label small">Raw JSON</label>
                        </div>
                      </div>

                      {showRawJson ? (
                        <pre className="bg-light p-3 rounded small max-h-[300px] overflow-y-auto">
                          {JSON.stringify(selectedDomainDetails, null, 2)}
                        </pre>
                      ) : (
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-sm-6">
                                <small className="text-muted d-block">Ownership</small>
                                <span className={`badge ${selectedDomainDetails.info?.dns_status === 'connected' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                  {selectedDomainDetails.info?.dns_status === 'connected'
                                    ? 'Verified'
                                    : (selectedDomainDetails.info?.dns_status || 'Unverified')}
                                </span>
                              </div>
                              <div className="col-sm-6">
                                <small className="text-muted d-block">Status</small>
                                <span className={`badge ${(selectedDomainDetails.info?.domain_status === '1' || selectedDomainDetails.info?.domain_status === 'active')
                                  ? 'bg-success'
                                  : (selectedDomainDetails.info?.domain_status === '0' || selectedDomainDetails.info?.domain_status === 'inactive')
                                    ? 'bg-danger'
                                    : 'bg-secondary'
                                  }`}>
                                  {(selectedDomainDetails.info?.domain_status === '1' || selectedDomainDetails.info?.domain_status === 'active')
                                    ? 'Active'
                                    : (selectedDomainDetails.info?.domain_status === '0' || selectedDomainDetails.info?.domain_status === 'inactive')
                                      ? 'Inactive'
                                      : (selectedDomainDetails.info?.domain_status || 'Unknown')
                                  }
                                </span>
                              </div>
                              <div className="col-sm-6">
                                <small className="text-muted d-block">Last Checked</small>
                                <strong>
                                  {selectedDomainDetails.info?.last_checked_at
                                    ? formatDateTime(selectedDomainDetails.info.last_checked_at)
                                    : 'Never'}
                                </strong>
                              </div>
                              {/* Add more fields as needed */}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <i className="las la-mouse-pointer fa-2x mb-2 opacity-50"></i>
                      <p>Select a domain to view details</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="las la-globe-americas fa-3x mb-3 opacity-50"></i>
                <p>No domains found for this user.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDomainsModal;
